import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import ModalFormEditor from './ModalFormEditor';

const renderFormElement = (element) => {
  const commonProps = {
    fullWidth: true,
    size: 'small',
    disabled: true, // Disabled in preview mode
    sx: { mb: 1 },
  };

  switch (element.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'url':
      return (
        <TextField
          {...commonProps}
          type={element.type}
          label={element.label}
          placeholder={element.placeholder}
          value={element.value || ''}
        />
      );

    case 'textarea':
      return (
        <TextField
          {...commonProps}
          multiline
          rows={element.rows || 4}
          label={element.label}
          placeholder={element.placeholder}
          value={element.value || ''}
        />
      );

    case 'number':
      return (
        <TextField
          {...commonProps}
          type="number"
          label={element.label}
          value={element.value || ''}
        />
      );

    case 'checkbox':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input type="checkbox" checked={element.value || false} disabled />
          <Typography variant="body2">{element.label}</Typography>
        </Box>
      );

    default:
      return (
        <Typography variant="body2" color="text.secondary">
          {element.label}
          {' '}
          (
          {element.type}
          )
        </Typography>
      );
  }
};

function DraggableFormElement({
  element,
  index,
  moveFormElement,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  isSelected,
  onSelect,
}) {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'form-element',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveFormElement(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'form-element',
    item: () => ({ id: element.id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const getElementIcon = (type) => {
    const icons = {
      text: '📝',
      textarea: '📄',
      number: '🔢',
      checkbox: '☑️',
      radiobuttons: '🔘',
      listbox: '📋',
      date: '📅',
      datetime: '⏰',
      combobox: '📦',
      grid: '📊',
      groupbox: '📁',
    };
    return icons[type] || '📝';
  };

  return (
    <div ref={ref}>
      <Paper
        elevation={isDragging ? 4 : 1}
        sx={{
          p: 2,
          mb: 2,
          cursor: isDragging ? 'grabbing' : 'pointer',
          opacity: isDragging ? 0.7 : 1,
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          '&:hover': {
            borderColor: 'primary.main',
            elevation: 2,
            '& .delete-button': {
              opacity: 1,
            },
          },
        }}
        onClick={(e) => {
          if (isDragging) return;
          const target = e.target.closest('button');
          if (target) return;
          e.stopPropagation();
          console.log('Element clicked:', element);
          onSelect && onSelect(element);
        }}
      >
        {/* Form element rendering */}
        <Box sx={{ minHeight: 40, pointerEvents: 'none' }}>
          {renderFormElement(element)}
        </Box>

        {/* Drag indicator */}
        <Box
          ref={drag}
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0.3,
            cursor: 'grab',
            padding: 1,
            '&:hover': { opacity: 0.7 },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        {/* Delete button - more subtle design */}
        <IconButton
          className="delete-button"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(element.id);
          }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 2,
            opacity: 0,
            transition: 'all 0.2s ease',
            bgcolor: 'error.main',
            color: 'white',
            width: 20,
            height: 20,
            '&:hover': {
              bgcolor: 'error.dark',
              transform: 'scale(1.1)',
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Paper>
    </div>
  );
}

// Add GridDropZone component for grid/table container
function GridDropZone({
  element, gridIndex, onDropToGrid, onRemoveColumn, onOpenEditModal,
}) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['widget', 'form-element'],
    canDrop: (item) => item.type !== 'grid' && item.type !== 'groupbox',
    drop: (item, monitor) => {
      if (monitor.didDrop()) return;
      onDropToGrid(gridIndex, item);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  return (
    <Box
      ref={drop}
      sx={(theme) => ({
        border: '2px dashed #888',
        borderRadius: 1,
        p: 2,
        mt: 1,
        mb: 2,
        background: isOver && canDrop
          ? (theme.palette.mode === 'dark' ? '#222' : theme.palette.action.hover)
          : (theme.palette.mode === 'dark' ? '#181818' : theme.palette.background.default),
        minHeight: 80,
      })}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
        {element.label || 'Grid/Table'}
      </Typography>
      {(element.columns && element.columns.length > 0) ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {element.columns.map((col, idx) => (
            <Box
              key={col.id}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                p: 1,
                border: '1px solid #444',
                borderRadius: 1,
                background: theme.palette.mode === 'dark' ? '#232323' : theme.palette.background.paper,
                color: 'text.primary',
                minWidth: 120,
                mb: 1,
                mr: 1,
                cursor: 'pointer',
              })}
              onClick={(e) => { e.stopPropagation(); onOpenEditModal(col); }}
              title="Edit column"
            >
              <Box sx={{ flex: 1 }}>
                <Typography component="span" sx={{ fontSize: '0.875rem' }}>
                  {col.label}
                </Typography>
                {' '}
                <Typography component="span" sx={{ color: '#888', fontSize: '0.75rem' }}>
                  (
                  {col.type}
                  )
                </Typography>
              </Box>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => { e.stopPropagation(); onRemoveColumn(gridIndex, col.id); }}
                sx={{ ml: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 14 }}>
          Drag fields here to add columns
        </Typography>
      )}
    </Box>
  );
}

// GroupBoxDropZone component for groupbox container
function GroupBoxDropZone({
  element, groupboxIndex, onDropToGroupbox, onRemoveField, onOpenEditModal, formElements,
}) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['widget', 'form-element'],
    canDrop: (item) => item.type !== 'grid' && item.type !== 'groupbox',
    drop: (item, monitor) => {
      if (monitor.didDrop()) return;
      onDropToGroupbox(groupboxIndex, item);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  return (
    <Box
      ref={drop}
      sx={(theme) => ({
        border: '2px dashed #888',
        borderRadius: 1,
        p: 2,
        mt: 1,
        mb: 2,
        background: isOver && canDrop
          ? (theme.palette.mode === 'dark' ? '#222' : theme.palette.action.hover)
          : (theme.palette.mode === 'dark' ? '#181818' : theme.palette.background.default),
        minHeight: 80,
      })}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
        {element.label || 'Group Box'}
      </Typography>
      {(element.fields && element.fields.length > 0) ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {element.fields.map((fieldId, idx) => {
            const field = formElements.find((f) => f.id === fieldId);
            if (!field) return null;
            return (
              <Box
                key={fieldId}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  background: theme.palette.mode === 'dark' ? '#232323' : theme.palette.background.paper,
                  color: 'text.primary',
                  minHeight: 40,
                  cursor: 'pointer',
                  gap: 1,
                })}
                onClick={(e) => { e.stopPropagation(); onOpenEditModal(field); }}
                title="Edit field"
              >
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {field.label}
                  </Typography>
                  {field.type && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      (
                      {field.type}
                      )
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => { e.stopPropagation(); onRemoveField(groupboxIndex, fieldId); }}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 14 }}>
          Drag fields here to add to group
        </Typography>
      )}
    </Box>
  );
}

function FormEditor({
  formElements,
  setFormElements,
  handleOpenModal,
  moveFormElement,
  onElementSelect,
  selectedElement,
  onSaveStateForUndo, // Add this prop for undo support
}) {
  // Add local modal state to bypass FormBuilder's modal handling
  const [localModalOpen, setLocalModalOpen] = useState(false);
  const [localSelectedElement, setLocalSelectedElement] = useState(null);

  // Grid dialog state
  const [gridDialogOpen, setGridDialogOpen] = useState(false);
  const [gridColumns, setGridColumns] = useState([]);
  const [editingGridIndex, setEditingGridIndex] = useState(null);

  const handleDeleteElement = (elementId) => {
    // Save state before deletion for undo
    onSaveStateForUndo && onSaveStateForUndo();
    setFormElements((prev) => prev.filter((el) => el.id !== elementId));
    if (selectedElement && selectedElement.id === elementId) {
      onElementSelect && onElementSelect(null);
    }
  };

  const handleDuplicateElement = (element) => {
    // Save state before duplication for undo
    onSaveStateForUndo && onSaveStateForUndo();
    const duplicatedElement = {
      ...element,
      id: `${element.type}_${Date.now()}`,
      label: `${element.label} (Copy)`,
    };
    setFormElements((prev) => [...prev, duplicatedElement]);
  };

  const handleToggleVisibility = (elementId) => {
    setFormElements((prev) => prev.map((el) => (el.id === elementId
      ? { ...el, hidden: !el.hidden }
      : el)));
  };

  // Handle edit button clicks - use properties panel for grids, modal for others
  const handleEditElement = (element) => {
    console.log('FormEditor handleEditElement called with:', element);

    if (!element) {
      console.error('FormEditor: No element provided to handleEditElement');
      return;
    }

    // For grid fields, use the properties panel instead of modal
    if (element.type === 'grid') {
      console.log('Grid element selected, using properties panel');
      onElementSelect && onElementSelect(element);
      return;
    }

    // For other elements, use the local modal
    try {
      const elementToEdit = {
        id: element.id,
        type: element.type || 'text',
        label: element.label || `${element.type || 'text'} field`,
        placeholder: element.placeholder || '',
        required: Boolean(element.required),
        hidden: Boolean(element.hidden),
        options: Array.isArray(element.options) ? element.options : [],
        value: element.value || '',
        ...element,
      };

      console.log('Opening local modal with element:', elementToEdit);
      setLocalSelectedElement(elementToEdit);
      setLocalModalOpen(true);
    } catch (error) {
      console.error('Error in handleEditElement:', error);
    }
  };

  const handleLocalModalClose = () => {
    setLocalModalOpen(false);
    setLocalSelectedElement(null);
  };

  const handleLocalModalSave = (updatedElement) => {
    console.log('Saving updated element:', updatedElement);
    setFormElements((prev) => prev.map((el) => (el.id === updatedElement.id ? updatedElement : el)));
    handleLocalModalClose();
  };

  // Grid/Table handlers
  const handleDropToGrid = (gridIndex, item) => {
    if (item.type === 'grid' || item.type === 'groupbox') return;

    onSaveStateForUndo && onSaveStateForUndo();

    // Always use the actual field definition (widget or element)
    const field = item.widget ? item.widget : item;

    // Use a unique base for id/key, fallback to 'field'
    const base = typeof field.type === 'string' && field.type.trim()
      ? field.type
      : 'field';

    // Use the original label if available, fallback to placeholder, then type, then 'Column'
    let label = '';
    if (typeof field.label === 'string' && field.label.trim()) {
      label = field.label;
    } else if (typeof field.placeholder === 'string' && field.placeholder.trim()) {
      label = field.placeholder;
    } else if (typeof field.type === 'string' && field.type.trim()) {
      label = field.type.charAt(0).toUpperCase() + field.type.slice(1);
    } else {
      label = 'Column';
    }

    // Use the original type if available, fallback to 'text'
    let type = 'text';
    if (typeof field.type === 'string' && field.type.trim()) {
      type = field.type;
    }

    // Copy all relevant properties, but do not overwrite id/key/type/label
    const {
      id, key, label: _label, type: _type, ...rest
    } = field;

    const unique = Date.now();

    const newColumn = {
      id: `${base}_${unique}`,
      key: `${base}_${unique}`,
      label,
      type,
      ...rest,
    };

    setFormElements((prev) => prev.map((el, idx) => (idx === gridIndex
      ? {
        ...el,
        columns: [...(el.columns || []), newColumn],
      }
      : el)));
  };

  const handleDropToGroupbox = (groupboxIndex, item) => {
    if (item.type === 'grid' || item.type === 'groupbox') return;

    const existingField = formElements.find((f) => f.id === item.id);
    let newFieldId = item.id;
    let newFieldObj = null;

    if (!existingField) {
      newFieldId = item.id || (`${item.type}_${Date.now()}`);
      newFieldObj = {
        id: newFieldId,
        type: item.type,
        label: item.label || item.type,
        value: item.defaultValue || '',
        ...item,
      };
    }

    setFormElements((prev) => {
      let updated = prev;
      if (newFieldObj) {
        updated = [...prev, newFieldObj];
      }
      return updated.map((el, idx) => (idx === groupboxIndex
        ? {
          ...el,
          fields: [...(el.fields || []), newFieldId],
        }
        : el));
    });
  };

  const removeGridColumn = (gridIndex, columnId) => {
    // Save state before removal for undo
    onSaveStateForUndo && onSaveStateForUndo();
    setFormElements((prev) => prev.map((el, idx) => (idx === gridIndex
      ? { ...el, columns: (el.columns || []).filter((col) => col.id !== columnId) }
      : el)));
  };

  const removeGroupboxField = (groupboxIndex, fieldId) => {
    // Save state before removal for undo
    onSaveStateForUndo && onSaveStateForUndo();
    setFormElements((prev) => prev.map((el, idx) => (idx === groupboxIndex
      ? { ...el, fields: (el.fields || []).filter((fid) => fid !== fieldId) }
      : el)));
  };

  // Add drop zone for new elements from sidebar
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['widget', 'form-element'],
    drop: (item, monitor) => {
      // Only call onSaveStateForUndo if this is the top-level drop (not a nested drop)
      if (!monitor.didDrop()) {
        onSaveStateForUndo && onSaveStateForUndo();
        // Handle new widget drops from sidebar
        if (item.widget) {
          const newElement = {
            ...item.widget,
            id: `${item.widget.type}_${Date.now()}`,
            label: item.widget.label || item.widget.type,
          };
          setFormElements((prev) => [...prev, newElement]);
        }
        // Handle direct item drops (from reorder or external)
        else if (item.type && !item.widget) {
          // If this is a reorder (item has index), do not add a new element
          if (typeof item.index === 'number') {
            // Do nothing here; the reorder logic is handled elsewhere (moveFormElement)
          } else {
            // This is a new element drop (external)
            const newElement = {
              ...item,
              id: `${item.type}_${Date.now()}`,
              label: item.label || item.type,
            };
            setFormElements((prev) => [...prev, newElement]);
          }
        }
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true }),
      canDrop: !!monitor.canDrop(),
    }),
  });

  if (formElements.length === 0) {
    return (
      <Box
        ref={drop}
        sx={{
          textAlign: 'center',
          py: 8,
          border: '2px dashed',
          borderColor: isOver && canDrop ? 'primary.main' : 'divider',
          borderRadius: 2,
          bgcolor: isOver && canDrop ? 'primary.50' : 'background.paper',
          transition: 'all 0.2s ease',
        }}
      >
        <AddIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {isOver && canDrop ? 'Drop component here' : 'No form elements yet'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Start building your form by dragging components from the sidebar
        </Typography>
        <Typography variant="caption" color="text.secondary">
          💡 Tip: You can drag and drop to reorder elements once you add them
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={drop}
      sx={{
        minHeight: '100%',
        maxWidth: 800,
        mx: 'auto',
        border: isOver && canDrop ? '2px dashed' : 'none',
        borderColor: 'primary.main',
        borderRadius: 2,
        p: isOver && canDrop ? 2 : 0,
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Form Elements (
          {formElements.length}
          )
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag elements to reorder • Click to select • Use the properties panel to edit
        </Typography>
      </Box>

      <Box>
        {formElements
          .filter((element) => {
            if (element.type === 'groupbox' || element.type === 'grid') return true;
            const inAnyGroupbox = formElements.some(
              (el) => el.type === 'groupbox' && (el.fields || []).includes(element.id),
            );
            return !inAnyGroupbox;
          })
          .map((element, index) => (element.type === 'grid' ? (
            <Box key={element.id} sx={{ position: 'relative' }}>
              <Paper
                sx={{
                  position: 'relative',
                  '&:hover .grid-delete-button': {
                    opacity: 1,
                  },
                }}
              >
                <IconButton
                  className="grid-delete-button"
                  size="small"
                  onClick={() => handleDeleteElement(element.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 3,
                    opacity: 0,
                    transition: 'all 0.2s ease',
                    bgcolor: 'error.main',
                    color: 'white',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      bgcolor: 'error.dark',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <GridDropZone
                  element={element}
                  gridIndex={index}
                  onDropToGrid={handleDropToGrid}
                  onRemoveColumn={removeGridColumn}
                  onOpenEditModal={onElementSelect}
                />
              </Paper>
            </Box>
          ) : element.type === 'groupbox' ? (
            <Box key={element.id} sx={{ position: 'relative' }}>
              <Paper
                sx={{
                  position: 'relative',
                  '&:hover .groupbox-delete-button': {
                    opacity: 1,
                  },
                }}
              >
                <IconButton
                  className="groupbox-delete-button"
                  size="small"
                  onClick={() => handleDeleteElement(element.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 3,
                    opacity: 0,
                    transition: 'all 0.2s ease',
                    bgcolor: 'error.main',
                    color: 'white',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      bgcolor: 'error.dark',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <GroupBoxDropZone
                  element={element}
                  groupboxIndex={index}
                  onDropToGroupbox={handleDropToGroupbox}
                  onRemoveField={removeGroupboxField}
                  onOpenEditModal={handleEditElement}
                  formElements={formElements}
                />
              </Paper>
            </Box>
          ) : (
            <DraggableFormElement
              key={element.id}
              element={element}
              index={index}
              moveFormElement={moveFormElement}
              onEdit={handleEditElement}
              onDelete={handleDeleteElement}
              onDuplicate={handleDuplicateElement}
              onToggleVisibility={handleToggleVisibility}
              isSelected={selectedElement && selectedElement.id === element.id}
              onSelect={onElementSelect}
            />
          )))}
      </Box>

      {/* Drop indicator when dragging over */}
      {isOver && canDrop && (
        <Box sx={{
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          p: 2,
          mt: 2,
          textAlign: 'center',
          bgcolor: 'primary.50',
        }}
        >
          <Typography variant="body2" color="primary">
            Drop here to add component
          </Typography>
        </Box>
      )}

      {/* Form Actions */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 4,
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag more components from the sidebar to add them here
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            // Focus on the widget sidebar for better UX
            const sidebar = document.querySelector('[data-widget-sidebar]');
            if (sidebar) {
              sidebar.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          Add Components
        </Button>
      </Paper>

      {/* Local Modal Editor */}
      {localModalOpen && localSelectedElement && (
        <ModalFormEditor
          isOpen={localModalOpen}
          onClose={handleLocalModalClose}
          element={localSelectedElement}
          onSave={handleLocalModalSave}
        />
      )}
    </Box>
  );
}

export default FormEditor;
